const state = {
  tiers: {}, // Keyed by programId: { [programId]: [tiers] }
  loading: false,
  error: null
}

const mutations = {
  SET_TIERS (state, { programId, tiers }) {
    state.tiers = {
      ...state.tiers,
      [programId]: tiers
    }
  },
  ADD_TIER (state, { programId, tier }) {
    const programTiers = state.tiers[programId] || []
    state.tiers = {
      ...state.tiers,
      [programId]: [...programTiers, tier]
    }
  },
  UPDATE_TIER (state, { programId, tier }) {
    const programTiers = state.tiers[programId] || []
    const index = programTiers.findIndex(t => t.id === tier.id)
    if (index !== -1) {
      programTiers.splice(index, 1, tier)
      state.tiers = {
        ...state.tiers,
        [programId]: [...programTiers]
      }
    }
  },
  REMOVE_TIER (state, { programId, tierId }) {
    const programTiers = state.tiers[programId] || []
    state.tiers = {
      ...state.tiers,
      [programId]: programTiers.filter(t => t.id !== tierId)
    }
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_ERROR (state, error) {
    state.error = error
  }
}

const actions = {
  async fetchTiers ({ commit }, programId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const tiers = await this._vm.$loyaltyService.getTiers(programId)
      // Sort by sort_order, then min_points_required
      const sortedTiers = tiers.sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return (a.sort_order || 0) - (b.sort_order || 0)
        }
        return a.min_points_required - b.min_points_required
      })
      commit('SET_TIERS', { programId, tiers: sortedTiers })
      return sortedTiers
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createTier ({ commit }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const tier = await this._vm.$loyaltyService.createTier(data)
      commit('ADD_TIER', { programId: data.loyalty_program_id, tier })
      return tier
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateTier ({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const tier = await this._vm.$loyaltyService.updateTier(id, data)
      commit('UPDATE_TIER', { programId: data.loyalty_program_id || tier.loyalty_program_id, tier })
      return tier
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteTier ({ commit }, { id, programId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await this._vm.$loyaltyService.deleteTier(id)
      commit('REMOVE_TIER', { programId, tierId: id })
      return true
    } catch (error) {
      commit('SET_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  tiersByProgram: state => programId => {
    return state.tiers[programId] || []
  },
  nextTier: (state, getters) => (programId, currentPoints) => {
    const tiers = getters.tiersByProgram(programId)
    if (!tiers || tiers.length === 0) return null

    // Find current tier
    const currentTier = tiers
      .filter(t => t.is_active)
      .sort((a, b) => b.min_points_required - a.min_points_required)
      .find(t => currentPoints >= t.min_points_required)

    if (!currentTier) return null

    // Find next tier
    const nextTier = tiers
      .filter(t => t.is_active && t.min_points_required > currentTier.min_points_required)
      .sort((a, b) => a.min_points_required - b.min_points_required)[0]

    return nextTier || null
  },
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
